export type AgentDocument = {
  id: number;
  source_type: 'text' | 'url' | 'file';
  source_name: string;
  source_content: string | null;
  status: 'pending' | 'uploaded' | 'ready' | 'failed';
  error_message: string | null;
  created_at: string;
};

export type IntegrationStatus = {
  eleven_labs_agent_id: string | null;
  published: boolean;
  post_call_webhook: boolean;
  post_call_webhook_id: string | null;
  webhook_secret_set: boolean;
  initiation_webhook: boolean;
  initiation_webhook_url: string | null;
  monitoring_enabled: boolean;
  multilingual: boolean;
  language_count: number;
};

export type ElevenLabsModel =
  | 'eleven_flash_v2_5'
  | 'eleven_turbo_v2'
  | 'eleven_turbo_v2_5'
  | 'eleven_multilingual_v2';

// Labels live in the `agents` dictionary under `models.<value>`, so this stays a plain list of API values.
export const ELEVEN_LABS_MODELS: ElevenLabsModel[] = [
  'eleven_flash_v2_5',
  'eleven_turbo_v2',
  'eleven_turbo_v2_5',
  'eleven_multilingual_v2',
];

export type ConversationalAgent = {
  id: number;
  name: string;
  system_prompt: string | null;
  first_message: string | null;
  language: string;
  multilingual: boolean;
  voice_id: string | null;
  voice_settings: Record<string, unknown>;
  eleven_labs_model: ElevenLabsModel | null;
  publish_status: string;
  publish_error: string | null;
  published_at: string | null;
  monthly_budget_minutes: number | null;
  budget_alert_threshold: number;
  budget_action: string;
  monthly_usage_minutes?: number;
  budget_usage_percent?: number;
  integration_status?: IntegrationStatus;
  twilio_phone_number_id: number | null;
  twilio_phone_number?: { id: number; phone_number: string; friendly_name: string } | null;
  conversational_agent_documents: AgentDocument[];
  created_at: string;
  updated_at: string;
};

export type AgentsResponse = {
  conversational_agents: ConversationalAgent[];
};

export type AgentResponse = {
  conversational_agent: ConversationalAgent;
};

export type CreateAgentPayload = {
  company_id: number;
  name: string;
  system_prompt?: string;
  first_message?: string;
  language?: string;
  multilingual?: boolean;
  voice_id?: string;
  twilio_phone_number_id?: number;
  monthly_budget_minutes?: number;
  budget_alert_threshold?: number;
  budget_action?: string;
};

export type UpdateAgentPayload = {
  name?: string;
  system_prompt?: string;
  first_message?: string;
  language?: string;
  multilingual?: boolean;
  voice_id?: string;
  voice_settings?: Record<string, unknown>;
  eleven_labs_model?: ElevenLabsModel | null;
  twilio_phone_number_id?: number | null;
  monthly_budget_minutes?: number | null;
  budget_alert_threshold?: number;
  budget_action?: string;
};

export type AgentUsage = {
  agent_id: number;
  monthly_usage_minutes: number;
  monthly_budget_minutes: number | null;
  budget_usage_percent: number;
  budget_action: string;
  daily_breakdown: { date: string; total_minutes: number; call_count: number }[];
};

export type AgentParams = {
  company_id: number;
};

export type TestCallPayload = {
  company_id: number;
  phone_number: string;
};

export type AddKbTextPayload = {
  company_id: number;
  name?: string;
  content: string;
};

export type AddKbUrlPayload = {
  company_id: number;
  name?: string;
  url: string;
};

export type AddKbFilePayload = {
  company_id: number;
  name?: string;
  file: File;
};

export type PrefillResult = {
  system_prompt: string;
  first_message: string;
  name: string;
};

export type TransferableDocument = {
  id: number;
  name: string;
  doc_type: string | null;
  created_at: string;
  updated_at: string;
};

export type TransferableDocumentsResponse = {
  documents: TransferableDocument[];
};

export type TransferKbPayload = {
  company_id: number;
  document_ids?: number[] | null;
};

export type TransferKbResponse = {
  transferred_count: number;
  documents: AgentDocument[];
};

export type UpdateCallHandlerPayload = {
  company_id: number;
  call_handler: 'rai' | 'elevenlabs';
  conversational_agent_id?: number;
};

export type LanguageVoice = {
  id: number;
  lang_code: string;       // human label, e.g. "German (DE)"
  locale_code: string;     // full locale, e.g. "de-de"
  iso_code: string;        // ISO 639-1 prefix, e.g. "de" — feed this to the ElevenLabs shared-library language filter
  voice_id: string;
  enabled: boolean;
};

export type LanguageVoicesResponse = {
  language_voices: LanguageVoice[];
};

export type UpdateLanguageVoicesPayload = {
  company_id: number;
  languages: { lang_code: string; enabled: boolean; voice_id: string | null }[];
};

export type CallHandlerResponse = {
  company: {
    id: number;
    name: string;
    call_handler: 'rai' | 'elevenlabs';
    active_conversational_agent_id: number | null;
  };
};

// Per-company migration flag for routing the generic call-center
// handoff into the Command Center queue (true) vs. PSTN dial (false).
// See RespondIT-CRM/docs/command_center_live_calls.md.
export type UpdateCommandCenterHandoffPayload = {
  company_id: number;
  enabled: boolean;
};

export type CommandCenterHandoffResponse = {
  company: {
    id: number;
    name: string;
    command_center_handoff_enabled: boolean;
  };
};

export type AudioClipType = 'disclaimer' | 'transferring' | 'filler';

export type TransferAnnouncement = {
  clip_type: AudioClipType;
  language: string;
  voice_id: string | null;
  source: 'company' | 'default' | null;
  status: 'ready' | 'missing';
  url: string | null;
};

export type TransferAnnouncementsResponse = {
  announcements: TransferAnnouncement[];
  texts: Partial<Record<AudioClipType, string>>;
};

export type AudioClipText = {
  clip_type: AudioClipType;
  text: string;
  default_text: string;
  customized: boolean;
  stale: boolean;
};

export type AudioClipTextsResponse = {
  texts: AudioClipText[];
};

export type FillerClip = {
  voice_id: string;
  lang_code: string;
  status: 'ready' | 'missing';
  reason?: string;
};

export type FillerClipsResponse = {
  clips: FillerClip[];
};

// 'valid'    — voice exists in the company's ElevenLabs workspace
// 'not_found'— voice ID is configured but workspace lookup returned no match (stale ID)
// 'unknown'  — workspace listing failed (network/quota); UI should degrade silently
export type VoiceHealthStatus = 'valid' | 'not_found' | 'unknown';

export type VoiceHealthResponse = {
  health: Record<string, VoiceHealthStatus>;
  workspace_total?: number;
  error?: string;
};
